'use client';

import {
  createContext,
  ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/**
 * A column's floor: its width while the columns exceed the rail and every rail scrolls together.
 * The class is the one source: the Tailwind scanner needs the literal, and the pixel value the
 * arrows snap by is read from it so the two cannot drift apart.
 */
export const COMPARE_COLUMN_WIDTH_CLASS = 'basis-[160px]';
export const COMPARE_COLUMN_WIDTH_PX = Number(
  /^basis-\[(\d+)px\]$/.exec(COMPARE_COLUMN_WIDTH_CLASS)?.[1],
);

export type ScrollSyncValue = {
  /** Registers a horizontally scrollable rail; returns the unregister. */
  register: (node: HTMLElement | null) => () => void;
  /** Whether content remains to the left / right of the current offset. */
  arrows: { left: boolean; right: boolean };
  /** Moves every rail by exactly one column, so a cell never lands mid-column. */
  scrollByColumn: (direction: -1 | 1) => void;
};

const ScrollSyncContext = createContext<ScrollSyncValue | null>(null);

export const useScrollSync = (): ScrollSyncValue | null =>
  useContext(ScrollSyncContext);

/**
 * Keeps every horizontally scrollable rail of the comparison page on one offset: the plan header
 * cards, each row of values, and the sticky drawer.
 *
 * This is the part of the mobile layout that can lie to the reader. Each rail scrolls on its own
 * element, so without this a value can sit under another plan's header — the page would look right
 * and be wrong. Mirroring is guarded against feedback: applying an offset to the other rails would
 * otherwise fire their own scroll handlers straight back.
 */
export const ScrollSyncProvider = ({
  children,
  resetKey,
}: {
  children: ReactNode;
  /** Changes when the compared set changes; the offset returns to the first column. */
  resetKey: string;
}) => {
  const rails = useRef(new Set<HTMLElement>());
  /**
   * The offset this provider last wrote onto the other rails. A `scroll` event is dispatched on a
   * later task, so a synchronous "currently applying" flag is already false by the time the mirrored
   * rails report back — and their stale offset would then be written onto the rail the reader is
   * still dragging, which is the snap-back on momentum scrolling. Comparing against the last written
   * offset identifies those echoes whenever they arrive.
   */
  const applied = useRef<number | null>(null);
  const [arrows, setArrows] = useState({ left: false, right: false });

  const readArrows = useCallback((node: HTMLElement) => {
    const max = node.scrollWidth - node.clientWidth;
    setArrows((previous) => {
      const next = {
        left: node.scrollLeft > 1,
        right: node.scrollLeft < max - 1,
      };
      return previous.left === next.left && previous.right === next.right
        ? previous
        : next;
    });
  }, []);

  const apply = useCallback((offset: number, source?: HTMLElement) => {
    applied.current = offset;
    rails.current.forEach((rail) => {
      if (rail !== source && rail.scrollLeft !== offset)
        rail.scrollLeft = offset;
    });
  }, []);

  const register = useCallback(
    (node: HTMLElement | null) => {
      if (!node) return () => undefined;

      const onScroll = () => {
        // An echo of our own write: the rails are already where this event says they are.
        if (applied.current === node.scrollLeft) return;
        apply(node.scrollLeft, node);
        readArrows(node);
      };

      rails.current.add(node);
      node.addEventListener('scroll', onScroll, { passive: true });

      // A rail mounted after the others adopts their offset instead of starting at zero.
      const [first] = Array.from(rails.current);
      if (first && first !== node) node.scrollLeft = first.scrollLeft;
      readArrows(node);

      // A rotation or resize changes what is left to scroll without any scroll event.
      const resized =
        typeof ResizeObserver === 'undefined'
          ? undefined
          : new ResizeObserver(() => readArrows(node));
      resized?.observe(node);

      return () => {
        resized?.disconnect();
        node.removeEventListener('scroll', onScroll);
        rails.current.delete(node);
      };
    },
    [apply, readArrows],
  );

  const scrollByColumn = useCallback(
    (direction: -1 | 1) => {
      const [first] = Array.from(rails.current);
      if (!first) return;
      const max = first.scrollWidth - first.clientWidth;
      // From the nearest column boundary, so a tap after a drag that stopped mid-column lands on a
      // column rather than carrying the misalignment along.
      const column = Math.round(first.scrollLeft / COMPARE_COLUMN_WIDTH_PX);
      const offset = Math.max(
        0,
        Math.min(max, (column + direction) * COMPARE_COLUMN_WIDTH_PX),
      );
      apply(offset);
      first.scrollLeft = offset;
      readArrows(first);
    },
    [apply, readArrows],
  );

  // Back to the first column — what a plan being added or removed calls for.
  const reset = useCallback(() => {
    apply(0);
    rails.current.forEach((rail) => {
      rail.scrollLeft = 0;
    });
    const [first] = Array.from(rails.current);
    if (first) readArrows(first);
  }, [apply, readArrows]);

  useEffect(() => {
    reset();
  }, [resetKey, reset]);

  const value = useMemo<ScrollSyncValue>(
    () => ({ register, arrows, scrollByColumn }),
    [register, arrows, scrollByColumn],
  );

  return (
    <ScrollSyncContext.Provider value={value}>
      {children}
    </ScrollSyncContext.Provider>
  );
};

/**
 * Attaches a rail to the shared offset. Returns the ref to put on the scrolling element.
 *
 * The callback identity never changes, on purpose: React re-invokes a ref callback whenever its
 * identity does, and the context value changes on every arrow update — a ref tied to it would
 * unregister and re-register the rail on each of its own scroll events.
 */
export const useScrollRail = () => {
  const sync = useScrollSync();
  const syncRef = useRef(sync);
  syncRef.current = sync;
  const cleanup = useRef<(() => void) | undefined>(undefined);

  return useCallback((node: HTMLElement | null) => {
    cleanup.current?.();
    cleanup.current = node ? syncRef.current?.register(node) : undefined;
  }, []);
};
